import React, { useMemo } from 'react'
import { Helmet } from 'react-helmet'

// Full-screen "coming soon" page. Rendered in place of the whole app while the
// COMING_SOON feature flag is on (see App.tsx / flagsService).

// Deterministic pseudo-random so the background layout is stable across renders.
function seeded(i: number) {
  const x = Math.sin(i * 9301 + 49297) * 233280
  return x - Math.floor(x)
}

function FloatingMarks() {
  const marks = useMemo(
    () =>
      Array.from({ length: 22 }, (_, i) => ({
        left: seeded(i) * 100,
        size: 1.5 + seeded(i + 100) * 5,
        duration: 18 + seeded(i + 200) * 22,
        delay: -seeded(i + 300) * 40,
        opacity: 0.04 + seeded(i + 400) * 0.1,
        drift: (seeded(i + 500) - 0.5) * 120,
      })),
    []
  )

  return (
    <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
      {marks.map((m, i) => (
        <span
          key={i}
          className="soon-mark absolute bottom-[-10vh] select-none font-bold text-white"
          style={
            {
              left: `${m.left}%`,
              fontSize: `${m.size}rem`,
              opacity: m.opacity,
              animationDuration: `${m.duration}s`,
              animationDelay: `${m.delay}s`,
              '--drift': `${m.drift}px`,
            } as React.CSSProperties
          }
        >
          ?
        </span>
      ))}
    </div>
  )
}

export default function Soon() {
  return (
    <div className="relative flex min-h-screen flex-col items-center overflow-hidden bg-[#070b14] px-4 py-10 text-center text-white">
      <Helmet>
        <title>3sual.az - Tezliklə</title>
      </Helmet>

      <style>{`
        @keyframes soon-rise {
          0%   { transform: translate(0, 0) rotate(-10deg); }
          50%  { transform: translate(var(--drift), -60vh) rotate(8deg); }
          100% { transform: translate(0, -125vh) rotate(-10deg); }
        }
        .soon-mark { animation: soon-rise linear infinite; }

        @keyframes soon-sway {
          0%, 100% { transform: translateX(-50%) rotate(-4deg); }
          50%      { transform: translateX(-50%) rotate(4deg); }
        }
        .soon-spotlight { animation: soon-sway 9s ease-in-out infinite; transform-origin: top center; }

        @media (prefers-reduced-motion: reduce) {
          .soon-mark, .soon-spotlight { animation: none; }
          .soon-mark { display: none; }
        }
      `}</style>

      {/* Stage spotlight from above */}
      <div
        aria-hidden
        className="soon-spotlight pointer-events-none absolute left-1/2 top-0 h-[90vh] w-[70vw] max-w-[900px]"
        style={{
          background:
            'radial-gradient(ellipse at top, rgba(96,165,250,0.22) 0%, rgba(96,165,250,0.06) 40%, transparent 70%)',
          clipPath: 'polygon(42% 0, 58% 0, 100% 100%, 0 100%)',
        }}
      />

      <FloatingMarks />

      <header className="relative z-10">
        <img src="/logo-horizontal-dark.png" alt="3sual.az" className="h-10 w-auto sm:h-12" />
      </header>

      <main className="relative z-10 my-auto flex flex-col items-center py-10">
        <h1 className="text-4xl font-bold tracking-tight text-white sm:text-6xl">Tezliklə</h1>
        <p className="mx-auto mt-4 max-w-lg text-base text-white/60 sm:text-lg">
          İntellektual oyunlar üçün sual bazası yenilənir.
        </p>
      </main>

      <footer className="relative z-10 text-sm text-white/40">© {new Date().getFullYear()} 3sual.az</footer>
    </div>
  )
}
